import { useId, useState } from 'react'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '#app/components/ui/collapsible.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { formatPublicDate } from '#app/utils/misc.tsx'

/**
 * Renders a published claim alongside the reviews that support it.
 *
 * Without this the citation requirement is a promise on /scoring-method/ that
 * a reader has to take on faith. Showing the source reviews makes it checkable,
 * which is the difference between an assertion and evidence. It is also what
 * an AI answer engine needs in order to cite the page rather than restate it.
 */

export interface EvidenceReview {
	id: string
	rating: number | null
	text: string
	authorName: string | null
	timestamp: string | null
}

export interface ClaimWithEvidence {
	text: string
	reviews: EvidenceReview[]
}

/** Review excerpts are trimmed to keep the expanded panel readable. */
const EXCERPT_CHARS = 260

function excerpt(text: string) {
	if (text.length <= EXCERPT_CHARS) return text
	const cut = text.slice(0, EXCERPT_CHARS)
	const lastSpace = cut.lastIndexOf(' ')
	return `${cut.slice(0, lastSpace > 0 ? lastSpace : EXCERPT_CHARS)}…`
}

// Fixed zone, not the runtime's: see formatPublicDate.
function formatDate(timestamp: string | null) {
	return formatPublicDate(timestamp, 'MMM yyyy')
}

export function ClaimEvidence({
	claim,
	tone = 'positive',
}: {
	claim: ClaimWithEvidence
	tone?: 'positive' | 'caution'
}) {
	const [open, setOpen] = useState(false)
	const contentId = useId()
	const count = claim.reviews.length

	return (
		/*
		 * No leading icon.
		 *
		 * Each claim previously carried a check or a warning triangle, which
		 * repeated what the section heading above it already said — "What
		 * reviewers consistently mention" and "Watch out for" are unambiguous, so
		 * the glyph added a column of decoration to every row and encoded
		 * nothing. Tone is carried by the rule on the caution group instead,
		 * where it applies once rather than per line.
		 */
		<li
			className={
				tone === 'caution'
					? 'border-destructive/40 border-l-2 pl-3 text-sm leading-relaxed'
					: 'border-muted-foreground/20 border-l-2 pl-3 text-sm leading-relaxed'
			}
		>
			<p className="text-foreground/90">{claim.text}</p>

			{count > 0 ? (
				<Collapsible onOpenChange={setOpen} open={open}>
					{/*
					 * The evidence trigger is the whole point of the component, so it
					 * reads as an affordance rather than a footnote: its own line, in
					 * the numeric face the rest of the site uses for counts.
					 */}
					<CollapsibleTrigger
						aria-controls={contentId}
						className="text-muted-foreground hover:text-foreground mt-1 inline-flex items-center gap-1 font-mono text-xs tabular-nums"
					>
						<Icon
							className={`size-3 transition-transform ${open ? 'rotate-180' : ''}`}
							name="chevron-down"
						/>
						{count} {count === 1 ? 'review' : 'reviews'}
					</CollapsibleTrigger>
					<CollapsibleContent id={contentId}>
						<ul className="mt-2 space-y-3">
							{claim.reviews.map((review) => {
								const date = formatDate(review.timestamp)
								return (
									<li key={review.id} className="text-xs">
										<div className="text-muted-foreground mb-1 flex flex-wrap gap-x-2 font-mono tabular-nums">
											{review.rating != null ? (
												<span>{review.rating.toFixed(1)}★</span>
											) : null}
											{review.authorName ? (
												<span className="font-sans">{review.authorName}</span>
											) : null}
											{date ? <span>{date}</span> : null}
										</div>
										<blockquote className="text-muted-foreground leading-relaxed">
											“{excerpt(review.text)}”
										</blockquote>
									</li>
								)
							})}
						</ul>
					</CollapsibleContent>
				</Collapsible>
			) : null}
		</li>
	)
}
